import { CONNECTION_ACTION, SUPPLIER_OF_GOODS } from 'constants/dataSets';
import {
  useCreateLogicalConnectionMutation,
  useCreateManufactureConnectionMutation,
  useCreateServiceConnectionMutation,
  useCreateSupplierConnectionMutation,
  useDeleteLogicalConnectionMutation,
  useDeleteManufactureConnectionMutation,
  useDeleteServiceConnectionMutation,
  useDeleteSupplierConnectionMutation,
  useUpdateLogicalConnectionMutation,
  useUpdateManufactureConnectionMutation,
  useUpdateServiceConnectionMutation,
  useUpdateSupplierConnectionMutation,
} from 'store/slices/valueChainMap/apis/valueChainMapApi';

import type { Connection, Edge } from '@xyflow/react';
import type IExpandedState from 'components/ConnectionVCModal/interfaces/IExpandedState';
import type { ISubmitConnection } from '../interfaces/ISubmitConnection';

const useCreateConnection = ({ selectedEdge }: { selectedEdge: Edge[] }) => {
  const [createLogicalConnection,
    { isLoading: isCreateLogicalConnectionLoading }] = useCreateLogicalConnectionMutation();
  const [updateLogicalConnection,
    { isLoading: isUpdateLogicalConnectionLoading }] = useUpdateLogicalConnectionMutation();
  const [deleteLogicalConnection,
    { isLoading: isDeleteLogicalConnectionLoading }] = useDeleteLogicalConnectionMutation();

  const [createSupplierConnection,
    { isLoading: isCreateSupplierConnectionLoading }] = useCreateSupplierConnectionMutation();
  const [updateSupplierConnection,
    { isLoading: isUpdateSupplierConnectionLoading }] = useUpdateSupplierConnectionMutation();
  const [deleteSupplierConnection,
    { isLoading: isDeleteSupplierConnectionLoading }] = useDeleteSupplierConnectionMutation();

  const [createServiceConnection,
    { isLoading: isCreateServiceConnectionLoading }] = useCreateServiceConnectionMutation();
  const [updateServiceConnection,
    { isLoading: isUpdateServiceConnectionLoading }] = useUpdateServiceConnectionMutation();
  const [deleteServiceConnection,
    { isLoading: isDeleteServiceConnectionLoading }] = useDeleteServiceConnectionMutation();

  const [createManufactureConnection,
    { isLoading: isCreateManufactureConnectionLoading }] = useCreateManufactureConnectionMutation();
  const [updateManufactureConnection,
    { isLoading: isUpdateManufactureConnectionLoading }] = useUpdateManufactureConnectionMutation();
  const [deleteManufactureConnection,
    { isLoading: isDeleteManufactureConnectionLoading }] = useDeleteManufactureConnectionMutation();

  const getNumber = (number: string): number => {
    if (!number) return 0;
    return Number(number.replace(/[,%]/g, ''));
  };

  // The transport methods are the only booleans the modal submits, so the checked ones are
  // exactly the truthy keys.
  const transportMethods = (values: ISubmitConnection) => Object.keys(values).filter(
    (key) => values[key as keyof ISubmitConnection] === true,
  );

  const createConnection = (
    values: ISubmitConnection,
    currentConnection: Connection | undefined,
    connection: IExpandedState,
    valueChainMapId?: string,
  ) => {
    if (connection.logical) {
      createLogicalConnection({
        valueChainMapId: Number(valueChainMapId),
        fromUnitId: Number(currentConnection?.source.split('_')[1]),
        toUnitId: Number(currentConnection?.target.split('_')[1]),
        connectionType: values?.connectionType?.value ?? null,
        comment: values?.commentLogical !== '' ? values?.commentLogical : null,
        ownership: values?.ownershipPercentage ? getNumber(values?.ownershipPercentage) : null,
      });
    }

    if (connection.supplier) {
      createSupplierConnection({
        valueChainMapId: Number(valueChainMapId),
        fromUnitId: Number(currentConnection?.source.split('_')[1]),
        toUnitId: Number(currentConnection?.target.split('_')[1]),
        transportMethods: transportMethods(values),
        distance: values?.distance !== '' ? values?.distance : null,
        monetaryValue: values?.monetaryVolumes ? getNumber(values?.monetaryVolumes) : null,
        monetaryCurrency: values?.unitMonetaryVolumes?.value ?? null,
        quantity: values?.quantity ? getNumber(values?.quantity) : null,
        quantityUnit: values?.unitCurrency?.value ?? null,
      });
    }

    if (connection.service) {
      createServiceConnection({
        valueChainMapId: Number(valueChainMapId),
        fromUnitId: Number(currentConnection?.source.split('_')[1]),
        toUnitId: Number(currentConnection?.target.split('_')[1]),
        serviceProviderType: values?.typeOfServiceProvider?.value ?? null,
        monetaryValue: values?.monetaryValues ? getNumber(values?.monetaryValues) : null,
        monetaryCurrency: values?.unitCurrencyValues?.value ?? null,
        comment: values?.commentService !== '' ? values?.commentService : null,
      });
    }

    if (connection.manufacture) {
      createManufactureConnection({
        valueChainMapId: Number(valueChainMapId),
        fromUnitId: Number(currentConnection?.source.split('_')[1]),
        toUnitId: Number(currentConnection?.target.split('_')[1]),
        connectionType: values?.manufactureConnectionType?.value ?? null,
        comment: values?.commentManufacture !== '' ? values?.commentManufacture : null,
      });
    }
  };

  const updateConnection = (
    values: ISubmitConnection,
    connection: IExpandedState,
    valueChainMapId?: string,
  ) => {
    const expanded: IExpandedState = {
      logical: false,
      supplier: false,
      service: false,
      manufacture: false,
    };
    selectedEdge.forEach((item) => {
      const type = item?.data?.connectionType;
      if (typeof type !== 'string') return;
      // The edge's type is `supplierOfGoods`, which the form keys as `supplier`.
      if (type === SUPPLIER_OF_GOODS) {
        expanded.supplier = true;
      } else if (type in expanded) {
        expanded[type as keyof IExpandedState] = true;
      }
    });
    const toId = values?.idToLogical
      || values?.idToSupplier
      || values?.idToService
      || values?.idToManufacture
      || 0;
    if (expanded.logical && connection.logical) {
      updateLogicalConnection({
        id: values?.idLogical ?? 0,
        toUnitId: values?.idToLogical ?? 0,
        connectionType: values?.connectionType?.value ?? null,
        comment: values?.commentLogical !== '' ? values?.commentLogical : null,
        ownership: values?.ownershipPercentage ? getNumber(values?.ownershipPercentage) : null,
      });
    }
    if (expanded.supplier && connection.supplier) {
      updateSupplierConnection({
        id: values?.idSupplier ?? 0,
        transportMethods: transportMethods(values),
        distance: values?.distance !== '' ? values?.distance : null,
        monetaryValue: values?.monetaryVolumes ? getNumber(values?.monetaryVolumes) : null,
        monetaryCurrency: values?.unitMonetaryVolumes?.value ?? null,
        quantity: values?.quantity ? getNumber(values?.quantity) : null,
        quantityUnit: values?.unitCurrency?.value ?? null,
      });
    }
    if (expanded.service && connection.service) {
      updateServiceConnection({
        id: values?.idService ?? 0,
        toUnitId: values?.idToService ?? 0,
        serviceProviderType: values?.typeOfServiceProvider?.value ?? null,
        monetaryValue: values?.monetaryValues ? getNumber(values?.monetaryValues) : null,
        monetaryCurrency: values?.unitCurrencyValues?.value ?? null,
        comment: values?.commentService !== '' ? values?.commentService : null,
      });
    }
    if (expanded.manufacture && connection.manufacture) {
      updateManufactureConnection({
        id: values?.idManufacture ?? 0,
        toUnitId: values?.idToManufacture ?? 0,
        connectionType: values?.manufactureConnectionType?.value ?? null,
        comment: values?.commentManufacture !== '' ? values?.commentManufacture : null,
      });
    }

    if (!expanded.logical && connection.logical) {
      createLogicalConnection({
        valueChainMapId: Number(valueChainMapId),
        fromUnitId: values?.idFrom ?? 0,
        toUnitId: toId,
        connectionType: values?.connectionType?.value ?? null,
        comment: values?.commentLogical !== '' ? values?.commentLogical : null,
        ownership: values?.ownershipPercentage ? getNumber(values?.ownershipPercentage) : null,
      });
    }
    if (!expanded.supplier && connection.supplier) {
      createSupplierConnection({
        valueChainMapId: Number(valueChainMapId),
        fromUnitId: values?.idFrom ?? 0,
        toUnitId: toId,
        transportMethods: transportMethods(values).length > 0 ? transportMethods(values) : null,
        distance: values?.distance !== '' ? values?.distance : null,
        monetaryValue: values?.monetaryVolumes ? getNumber(values?.monetaryVolumes) : null,
        monetaryCurrency: values?.unitMonetaryVolumes?.value ?? null,
        quantity: values?.quantity ? getNumber(values?.quantity) : null,
        quantityUnit: values?.unitCurrency?.value ?? null,
      });
    }
    if (!expanded.service && connection.service) {
      createServiceConnection({
        valueChainMapId: Number(valueChainMapId),
        fromUnitId: values?.idFrom ?? 0,
        toUnitId: toId,
        serviceProviderType: values?.typeOfServiceProvider?.value ?? null,
        monetaryValue: values?.monetaryValues ? getNumber(values?.monetaryValues) : null,
        monetaryCurrency: values?.unitCurrencyValues?.value ?? null,
        comment: values?.commentService !== '' ? values?.commentService : null,
      });
    }
    if (!expanded.manufacture && connection.manufacture) {
      createManufactureConnection({
        valueChainMapId: Number(valueChainMapId),
        fromUnitId: values?.idFrom ?? 0,
        toUnitId: toId,
        connectionType: values?.manufactureConnectionType?.value ?? null,
        comment: values?.commentManufacture !== '' ? values?.commentManufacture : null,
      });
    }

    if (expanded.logical && !connection.logical) {
      deleteLogicalConnection({
        id: values?.idLogical ?? 0,
      });
    }
    if (expanded.supplier && !connection.supplier) {
      deleteSupplierConnection({
        id: values?.idSupplier ?? 0,
      });
    }
    if (expanded.service && !connection.service) {
      deleteServiceConnection({
        id: values?.idService ?? 0,
      });
    }
    if (expanded.manufacture && !connection.manufacture) {
      deleteManufactureConnection({
        id: values?.idManufacture ?? 0,
      });
    }
  };

  const onActionConnect = (
    values: ISubmitConnection,
    currentConnection: Connection | undefined,
    connection: IExpandedState,
    valueChainMapId?: string,
    action?: string,
  ) => {
    if (action === CONNECTION_ACTION.CREATE) {
      createConnection(values, currentConnection, connection, valueChainMapId);
    }
    if (action === CONNECTION_ACTION.UPDATE) {
      updateConnection(values, connection, valueChainMapId);
    }
  };

  return {
    isCreateLogicalConnectionLoading,
    isUpdateLogicalConnectionLoading,
    isDeleteLogicalConnectionLoading,
    isCreateSupplierConnectionLoading,
    isUpdateSupplierConnectionLoading,
    isDeleteSupplierConnectionLoading,
    isCreateServiceConnectionLoading,
    isUpdateServiceConnectionLoading,
    isDeleteServiceConnectionLoading,
    isCreateManufactureConnectionLoading,
    isUpdateManufactureConnectionLoading,
    isDeleteManufactureConnectionLoading,
    onActionConnect,
  };
};

export default useCreateConnection;
